import { apiClient } from '@/core/api/client';
import { Endpoints } from '@/core/api/endpoints';
import { ForgotPasswordError, ForgotPasswordRequest, ForgotPasswordResponse, ResetPasswordError, ResetPasswordRequest, ResetPasswordResponse, SetForgotPasswordRequest, SetForgotPasswordResponse, VerifyOTPRequest, VerifyOTPResponse } from '../types';

/**
 * Service for handling forgot password functionality
 */
export class ForgotPasswordService {
    /**
     * Send OTP to user's email for password reset
     */
    static async sendResetLink(request: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
        try {
            const response = await apiClient.post<ForgotPasswordResponse, ForgotPasswordRequest>(
                Endpoints.auth.forgotOtp,
                request
            );
            return response;
        } catch (error) {
            // Handle API errors
            if (error instanceof Error) {
                throw {
                    code: 'API_ERROR',
                    message: error.message,
                } as ForgotPasswordError;
            }
            throw {
                code: 'UNKNOWN_ERROR',
                message: 'Failed to send OTP',
            } as ForgotPasswordError;
        }
    }

    /**
     * Verify OTP for password reset
     */
    static async verifyOTP(request: VerifyOTPRequest): Promise<VerifyOTPResponse> {
        try {
            const response = await apiClient.post<VerifyOTPResponse, VerifyOTPRequest>(
                Endpoints.auth.verifyForgotOtp,
                request
            );
            return response;
        } catch (error: any) {
            // Handle API errors - check for error response format
            // If error response has error field, return it as response instead of throwing
            if (error?.response?.data?.error) {
                return {
                    status: 'error',
                    error: error.response.data.error,
                } as VerifyOTPResponse;
            }
            if (error instanceof Error) {
                throw {
                    code: 'API_ERROR',
                    message: error.message,
                } as ForgotPasswordError;
            }
            throw {
                code: 'UNKNOWN_ERROR',
                message: 'Failed to verify OTP',
            } as ForgotPasswordError;
        }
    }

    /**
     * Set forgot password after OTP verification (new API)
     */
    static async setForgotPassword(request: SetForgotPasswordRequest): Promise<SetForgotPasswordResponse> {
        try {
            const response = await apiClient.post<SetForgotPasswordResponse, SetForgotPasswordRequest>(
                Endpoints.auth.setForgotPassword,
                request
            );
            return response;
        } catch (error: any) {
            // Handle API errors - return error response instead of throwing
            if (error?.response?.data) {
                const errorData = error.response.data;
                return {
                    status: 'error',
                    error: errorData.error || errorData.message || 'Failed to set password',
                } as SetForgotPasswordResponse;
            }
            if (error instanceof Error) {
                return {
                    status: 'error',
                    error: error.message,
                } as SetForgotPasswordResponse;
            }
            return {
                status: 'error',
                error: 'Failed to set password',
            } as SetForgotPasswordResponse;
        }
    }

    /**
     * Reset password after OTP verification (forgot password flow)
     */
    static async resetPassword(request: ResetPasswordRequest): Promise<ResetPasswordResponse> {
        try {
            const response = await apiClient.post<ResetPasswordResponse, ResetPasswordRequest>(
                Endpoints.auth.resetPassword,
                request
            );
            return response;
        } catch (error: any) {
            // Handle API errors
            if (error?.response?.data) {
                const errorData = error.response.data as ResetPasswordError;
                if (errorData.error) {
                    throw {
                        code: 'API_ERROR',
                        message: errorData.error,
                    } as ForgotPasswordError;
                } else if (errorData.message) {
                    throw {
                        code: 'API_ERROR',
                        message: errorData.message,
                    } as ForgotPasswordError;
                } else if (errorData.errors && errorData.errors.length > 0 && errorData.errors[0]) {
                    const firstError = errorData.errors[0];
                    throw {
                        code: 'VALIDATION_ERROR',
                        message: firstError.message || 'Validation failed',
                        field: firstError.field,
                    } as ForgotPasswordError;
                }
            }
            if (error instanceof Error) {
                throw {
                    code: 'API_ERROR',
                    message: error.message,
                } as ForgotPasswordError;
            }
            throw {
                code: 'UNKNOWN_ERROR',
                message: 'Failed to reset password',
            } as ForgotPasswordError;
        }
    }
}